import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import { Popup } from "components"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge from "components/Lims/StatusBadge"
import CertificateOfAnalysis from "components/Lims/CertificateOfAnalysis"
import { limsRelease, limsResults, limsSampleQc, limsBatchHold, limsOos, message } from "state_management"
import { canSign, isGmpRelaxed } from "state_management/lims/segregationOfDuties"

const RS = limsRelease.RELEASE_STATUS

// QA Batch Release (SPEC-013, A3) — the LIMS-screen home for QA disposition of a lot/sample. A
// lot is "ready to release" once EVERY analysis on it is QA-approved (and none batch-rejected); QA
// signs ONE release e-signature and a batch certificate is issued. This screen shows two tables:
//   1) Ready to release — lots whose analyses are all QA-approved, with a QA-signed Release action.
//   2) Released — already-released lots with the batch certificate reachable from here.
// It complements the per-row Release + certificate on the Sample View. The empty right-column
// "boxes" of the old QA Release queue are gone (finding #2): only the two purposeful tables remain.
export default function QaBatchRelease() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const sampleGroupsRaw = useSelector((s) => s.sampleView.sampleGroups, shallowEqual)
  const sampleGroups = useMemo(() => sampleGroupsRaw || [], [sampleGroupsRaw])
  const resItems = useSelector((s) => s.limsResults.items, shallowEqual)
  const relItems = useSelector((s) => s.limsRelease.items, shallowEqual)
  const sampleQc = useSelector((s) => s.limsSampleQc, shallowEqual)
  const batchHoldItems = useSelector((s) => s.limsBatchHold.items, shallowEqual)
  // SPEC-018: the OOS register feeds the recoverable open-investigation ON_HOLD branch of the
  // release gate, so a lot with an open OOS investigation is correctly held back from "Ready to
  // release" here too (not only on the Sample View).
  const oosItems = useSelector((s) => s.limsOos.items, shallowEqual)
  const members = useSelector((s) => s.members)

  const [releasing, setReleasing] = useState(null) // the group pending a QA-signed release
  const [saving, setSaving] = useState(false)
  const [viewingCert, setViewingCert] = useState(null) // the released group whose certificate is open
  const [lifting, setLifting] = useState(null) // the batch-hold record pending a QA-signed lift, or null

  // K13 (b): active (not-yet-lifted) holds are the actionable list; lifted holds are shown as history
  // with their lift signature. The release gate (selectIsBatchRejected) already skips lifted holds.
  const activeHolds = useSelector(limsBatchHold.selectActiveHolds, shallowEqual)
  const liftedHolds = useSelector(limsBatchHold.selectLiftedHolds, shallowEqual)
  // The current member's QA gate (capability only — the per-hold four-eyes ≠ rejectedBy is folded in
  // per row below). Drives the ghosted "Lift hold" affordance (GMP: everyone SEES it, only QA acts).
  const qaCapabilityGate = useSelector((s) => canSign(s, { capability: "qa" }), shallowEqual)
  const currentMemberId = useSelector((s) => s.auth.memberId)
  const gmpRelaxed = useSelector(isGmpRelaxed) // SPEC-025: dev-only relaxation of the four-eyes guard

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    dispatch(limsResults.fetch())
    dispatch(limsRelease.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsBatchHold.fetch())
    dispatch(limsOos.fetch())
  }, [dispatch])

  // A minimal state slice for the release-status selector (which reads only these collections).
  const miniState = useMemo(
    () => ({
      limsResults: { items: resItems },
      limsRelease: { items: relItems },
      limsSampleQc: sampleQc,
      limsBatchHold: { items: batchHoldItems },
      limsOos: { items: oosItems },
    }),
    [resItems, relItems, sampleQc, batchHoldItems, oosItems]
  )

  // Lots ready to release: in the QC flow (≥1 result) and currently READY (all analyses QA-approved,
  // none batch-rejected, not yet released).
  const readyRows = useMemo(() => {
    const inQc = new Set(resItems.map((r) => r.sampleId))
    return sampleGroups
      .filter((g) => (g.analyses || []).some((a) => inQc.has(a.sampleId)))
      .map((g) => ({ key: limsRelease.sampleReleaseKey(g), g, status: limsRelease.selectSampleReleaseStatus(miniState, g) }))
      .filter((row) => row.status === RS.READY)
  }, [sampleGroups, resItems, miniState])

  // Released lots: one row per release record, joined back to its sample group (so the certificate
  // can read the analyses); falls back to the release record's own fields if the group is gone.
  const releasedRows = useMemo(() => {
    const groupByKey = {}
    sampleGroups.forEach((g) => {
      groupByKey[limsRelease.sampleReleaseKey(g)] = g
    })
    return relItems.map((rel) => ({
      rel,
      group:
        groupByKey[rel.id] ||
        { productName: rel.productName, sampleName: rel.sampleName, observationId: rel.observationId, analyses: [] },
    }))
  }, [sampleGroups, relItems])

  const initials = (id) => (id && members[id] && members[id].initials) || "—"
  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // SPEC-020 four-eyes on release: everyone who entered / reviewed / override-signed one of the
  // releasing group's QC results may not also sign the release.
  const releaseExclusions = useMemo(
    () => (releasing ? limsRelease.selectReleaseExclusions(miniState, releasing) : []),
    [releasing, miniState]
  )

  // Returns the promise so the e-signature dialog stays open and shows a backend 403 (wrong
  // password / capability / 4-eyes) or 422 (server-side release gate) inline. The SPEC-020
  // client-side gate revalidation still rejects a no-longer-READY lot with its own message.
  const handleRelease = (signature) => {
    const g = releasing
    setSaving(true)
    return dispatch(limsRelease.release(g, signature))
      .then(() => {
        setReleasing(null)
        dispatch(message.success(t("limsReleaseDone")))
      })
      .catch((err) => {
        if (limsRelease.isReleaseNotReadyError(err)) {
          err.message = t("limsReleaseNotReady")
        }
        throw err
      })
      .finally(() => setSaving(false))
  }

  // K13 (b): may the current user lift THIS hold? Needs the QA capability AND must not be the
  // person who placed the rejection (four-eyes ≠ rejectedBy). Returns the ghost reason key, or null.
  const liftGhostKey = (hold) => {
    if (!qaCapabilityGate.allowed) return "limsLiftGhostCapability"
    // SPEC-025: skip the four-eyes (≠ rejectedBy) guard when controls are relaxed (dev only).
    if (!gmpRelaxed && hold.rejectedBy && hold.rejectedBy === currentMemberId) return "limsLiftGhostFourEyes"
    return null
  }

  // K13 (b): lift the active hold under a QA signature with a MANDATORY reason. The original hold
  // record is preserved (the server stamps liftedBy/liftedAt/liftReason); a lifted hold stops
  // blocking release (the gate re-evaluates). Returns the promise so the dialog shows 403/404/409 inline.
  const handleLift = (signature) => {
    const hold = lifting
    setSaving(true)
    return dispatch(limsBatchHold.lift(hold.batchLot, signature))
      .then(() => {
        setLifting(null)
        dispatch(message.success(t("limsLiftDone", { lot: hold.batchLot })))
      })
      .finally(() => setSaving(false))
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaBatchRelease")}</h1>

      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsReadyToReleaseHeading")}</h2>
        {readyRows.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsReadyToReleaseEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsCoaProduct")}</th>
                <th>{t("limsCoaSample")}</th>
                <th>{t("limsCoaSampleId")}</th>
                <th>{t("limsReleaseStatusColumn")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {readyRows.map(({ key, g, status }) => (
                <tr key={key}>
                  <td>{g.productName || t("none")}</td>
                  <td>{g.sampleName || "—"}</td>
                  <td>{g.observationId != null ? g.observationId : "—"}</td>
                  <td>
                    <span className={"samplerow-release-badge is-" + status}>{t("limsReleaseStatusReady")}</span>
                  </td>
                  <td className="LimsQueue-actions">
                    <div className="LimsQueue-actions-inner">
                      <Button variant="success" size="sm" disabled={saving} onClick={() => setReleasing(g)}>
                        {t("limsReleaseAction")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {/* K13 (b): batch holds — the active holds (with a ghosted-for-non-QA "Lift hold" action) and,
          once lifted, the holds shown as history with their lift signature. A signed lift is a GMP
          correction: the hold record persists and the chain (rejected → lifted) stays visible. */}
      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsBatchHoldsHeading")}</h2>
        {activeHolds.length === 0 && liftedHolds.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsBatchHoldsEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsBatchHoldLot")}</th>
                <th>{t("limsBatchHoldRejectedBy")}</th>
                <th>{t("limsBatchHoldRejectedAt")}</th>
                <th>{t("limsBatchHoldReason")}</th>
                <th>{t("limsBatchHoldStatus")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeHolds.map((hold) => {
                const ghostKey = liftGhostKey(hold)
                return (
                  <tr key={hold.id}>
                    <td>{hold.batchLot || "—"}</td>
                    <td>{initials(hold.rejectedBy)}</td>
                    <td>{fmtDate(hold.rejectedAt)}</td>
                    <td>{hold.reason || "—"}</td>
                    <td>
                      <StatusBadge variant="fail">{t("limsBatchHoldStatusActive")}</StatusBadge>
                    </td>
                    <td className="LimsQueue-actions">
                      <div className="LimsQueue-actions-inner">
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={saving || !!ghostKey}
                          title={ghostKey ? t(ghostKey) : undefined}
                          onClick={() => setLifting(hold)}
                        >
                          {t("limsLiftAction")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {liftedHolds.map((hold) => (
                <tr key={hold.id} className="is-lifted">
                  <td>{hold.batchLot || "—"}</td>
                  <td>{initials(hold.rejectedBy)}</td>
                  <td>{fmtDate(hold.rejectedAt)}</td>
                  <td>{hold.reason || "—"}</td>
                  <td>
                    <StatusBadge
                      variant="neutral"
                      title={t("limsBatchHoldLiftedDetail", {
                        by: initials(hold.liftedBy),
                        at: fmtDate(hold.liftedAt),
                        reason: hold.liftReason || "—",
                      })}
                    >
                      {t("limsBatchHoldStatusLifted")}
                    </StatusBadge>
                    <div className="LimsQueue-lift-detail">
                      {t("limsBatchHoldLiftedBy", { by: initials(hold.liftedBy), at: fmtDate(hold.liftedAt) })}
                      {hold.liftReason ? ` — ${hold.liftReason}` : ""}
                    </div>
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsReleasedHeading")}</h2>
        {releasedRows.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsReleasedEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsCoaProduct")}</th>
                <th>{t("limsCoaSample")}</th>
                <th>{t("limsCoaSampleId")}</th>
                <th>{t("limsCoaReleasedBy")}</th>
                <th>{t("limsCoaReleasedAt")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {releasedRows.map(({ rel, group }) => (
                <tr key={rel.id}>
                  <td>{group.productName || t("none")}</td>
                  <td>{group.sampleName || "—"}</td>
                  <td>{group.observationId != null ? group.observationId : "—"}</td>
                  <td>{initials(rel.releasedBy)}</td>
                  <td>{fmtDate(rel.releasedAt)}</td>
                  <td className="LimsQueue-actions">
                    <div className="LimsQueue-actions-inner">
                      <Button variant="outline-secondary" size="sm" onClick={() => setViewingCert(group)}>
                        {t("limsCoaButton")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {releasing && (
        <ESignatureModal
          title={t("limsReleaseAction")}
          reasons={["released"]}
          requiredCapability="qa"
          excludedMemberIds={releaseExclusions}
          onSign={handleRelease}
          onCancel={() => setReleasing(null)}
        />
      )}

      {viewingCert && (
        <Popup title={t("limsCoaTitle")} onCancel={() => setViewingCert(null)}>
          <CertificateOfAnalysis group={viewingCert} />
        </Popup>
      )}

      {/* K13 (b): the QA lift signature — mandatory reason, four-eyes excludes the rejector. */}
      {lifting && (
        <ESignatureModal
          title={t("limsLiftAction")}
          reasons={["correction"]}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={lifting.rejectedBy ? [lifting.rejectedBy] : []}
          onSign={handleLift}
          onCancel={() => setLifting(null)}
        />
      )}
    </div>
  )
}
